
import React, { useState } from 'react';
import { X, PieChart as PieIcon, DollarSign } from 'lucide-react';

interface HoldingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any[];
  theme: 'dark' | 'light';
  icons?: Record<string, string>;
}

const BRAND_COLORS: Record<string, string> = {
  'BTC': '#f7931a', // Naranja Bitcoin
  'ETH': '#627eea', // Azul Ethereum
  'SOL': '#14f195', // Verde Solana
  'BNB': '#f3ba2f', // Amarillo BNB
};

const COLORS = [
  '#6366f1', // Indigo
  '#10b981', // Emerald
  '#ec4899', // Pink
  '#f59e0b', // Amber
  '#3b82f6', // Blue
  '#f43f5e', // Rose
  '#06b6d4', // Cyan
  '#8b5cf6', // Violet
  '#eab308', // Yellow
  '#14b8a6', // Teal
  '#a855f7', // Purple
  '#fb923c', // Light Orange
];

const HoldingsModal: React.FC<HoldingsModalProps> = ({ isOpen, onClose, data, theme, icons = {} }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (!isOpen) return null;

  const getIconUrl = (symbol: string) => {
    return icons[symbol.toUpperCase()] || '';
  };

  const activeHoldings = data
    .filter(m => m.currentVal > 0)
    .sort((a, b) => b.currentVal - a.currentVal);

  const totalPortfolioValue = activeHoldings.reduce((acc, curr) => acc + curr.currentVal, 0);

  let cumulativePercent = 0;
  const slices = activeHoldings.map((m, i) => {
    const symbol = m.ticket.split('-')[0].toUpperCase();
    const percent = (m.currentVal / totalPortfolioValue) * 100;
    const startPercent = cumulativePercent;
    cumulativePercent += percent;
    
    const color = BRAND_COLORS[symbol] || COLORS[i % COLORS.length];

    return {
      symbol,
      percent,
      startPercent,
      color,
      totalInvested: m.totalBuy,
      currentVal: m.currentVal
    };
  });

  const themeClasses = {
    overlay: "fixed inset-0 bg-black/80 backdrop-blur-md z-[110] flex items-center justify-center p-4 animate-in fade-in duration-300",
    container: `${theme === 'dark' ? 'bg-[#0b0c10] border-[#20232a]' : 'bg-white border-slate-200'} border w-full max-w-2xl max-h-[90vh] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200`,
    header: `flex items-center justify-between p-6 border-b ${theme === 'dark' ? 'border-[#20232a]' : 'border-slate-100'}`,
    textMain: theme === 'dark' ? 'text-white' : 'text-slate-900',
    textMuted: theme === 'dark' ? 'text-slate-500' : 'text-slate-400',
    scrollBar: theme === 'dark' ? 'custom-scrollbar-dark' : 'custom-scrollbar-light'
  };

  const currentHoveredData = hoveredIndex !== null ? slices[hoveredIndex] : null;

  return (
    <div className={themeClasses.overlay} onClick={onClose}>
      <div className={themeClasses.container} onClick={e => e.stopPropagation()}>
        <div className={themeClasses.header}>
          <div className="flex items-center space-x-4">
            <div className="bg-indigo-600 p-2.5 rounded-2xl shadow-lg shadow-indigo-600/20">
              <PieIcon size={22} className="text-white" />
            </div>
            <div>
              <h2 className={`text-xl font-black ${themeClasses.textMain} tracking-tight uppercase`}>Distribución de Cartera</h2>
              <p className={themeClasses.textMuted + " text-[10px] font-bold uppercase tracking-[0.2em]"}>Análisis de diversificación</p>
            </div>
          </div>
          <button onClick={onClose} className={`p-2 rounded-full ${theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-slate-100'} transition-colors`}>
            <X size={24} className={themeClasses.textMain} />
          </button>
        </div>

        <div className="p-8 flex flex-col md:flex-row items-center md:items-stretch justify-between md:space-x-12 space-y-10 md:space-y-0 overflow-hidden">
          <div className="flex flex-col justify-center items-center flex-shrink-0 md:w-1/2">
            <div className="relative w-64 h-64 sm:w-72 sm:h-72">
              <svg viewBox="0 0 42 42" className="w-full h-full transform -rotate-90 cursor-pointer">
                {slices.map((slice, i) => {
                  const radius = 15.915;
                  const strokeDasharray = `${slice.percent} ${100 - slice.percent}`;
                  const strokeDashoffset = -slice.startPercent;
                  const isHovered = hoveredIndex === i;
                  const isSomeHovered = hoveredIndex !== null;
                  
                  return (
                    <circle
                      key={i}
                      r={radius}
                      cx="21"
                      cy="21"
                      fill="transparent"
                      stroke={slice.color}
                      strokeWidth={isHovered ? "8.5" : "7.5"}
                      strokeDasharray={strokeDasharray}
                      strokeDashoffset={strokeDashoffset}
                      strokeOpacity={isSomeHovered ? (isHovered ? "1" : "0.2") : "1"}
                      className="transition-all duration-500 ease-out"
                      onMouseEnter={() => setHoveredIndex(i)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    />
                  );
                })}
              </svg>
              
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center px-6">
                {currentHoveredData ? (
                  <div className="animate-in fade-in zoom-in-95 duration-200 flex flex-col items-center">
                    <div className="w-12 h-12 mb-3 rounded-full bg-white/5 border border-white/10 shadow-xl overflow-hidden flex items-center justify-center relative">
                        <span className="text-sm font-black uppercase text-indigo-400">{currentHoveredData.symbol.charAt(0)}</span>
                        {getIconUrl(currentHoveredData.symbol) && (
                          <img 
                            src={getIconUrl(currentHoveredData.symbol)} 
                            className="absolute inset-0 w-full h-full object-contain bg-inherit" 
                            alt="" 
                            onError={(e) => (e.currentTarget.style.display = 'none')}
                          />
                        )}
                    </div>
                    <span className="block text-sm font-black uppercase tracking-widest mb-1" style={{ color: currentHoveredData.color }}>{currentHoveredData.symbol}</span>
                    <span className={`${themeClasses.textMain} block text-xl font-black leading-none`}>
                      ${currentHoveredData.currentVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <div className="mt-3 inline-block px-3 py-1 rounded-full text-[10px] font-black" style={{ backgroundColor: `${currentHoveredData.color}20`, color: currentHoveredData.color }}>
                      {currentHoveredData.percent.toFixed(1)}%
                    </div>
                  </div>
                ) : (
                  <div className="animate-in fade-in duration-500">
                    <span className={`${themeClasses.textMuted} text-[10px] font-black uppercase tracking-[0.3em]`}>Total</span>
                    <span className={`${themeClasses.textMain} block text-3xl font-black tracking-tighter`}>${totalPortfolioValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                    <span className={`${themeClasses.textMuted} block text-[9px] font-bold uppercase mt-2 tracking-widest`}>Valor Cartera</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={`flex-1 w-full max-h-[400px] overflow-y-auto pr-2 custom-scrollbar`}>
            <div className="flex flex-col space-y-1.5">
              {slices.map((slice, i) => {
                const isHovered = hoveredIndex === i;
                const iconUrl = getIconUrl(slice.symbol);
                return (
                  <div 
                    key={i} 
                    onMouseEnter={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    className={`flex items-center justify-between py-2.5 px-4 rounded-[1.2rem] transition-all duration-300 cursor-default border ${
                      isHovered 
                      ? (theme === 'dark' ? 'bg-white/5 border-white/10 shadow-xl' : 'bg-slate-50 border-slate-200 shadow-md') 
                      : 'border-transparent'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-black relative flex-shrink-0 overflow-hidden ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
                        <span className="uppercase">{slice.symbol.charAt(0)}</span>
                        {iconUrl && (
                          <img 
                            src={iconUrl} 
                            className="absolute inset-0 w-full h-full object-contain bg-inherit" 
                            alt=""
                            onError={(e) => (e.currentTarget.style.display = 'none')}
                          />
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className={`${themeClasses.textMain} font-black text-[13px] tracking-tight uppercase`}>
                            {slice.symbol}
                        </span>
                        <span className="text-[9px] font-bold text-slate-500 uppercase tracking-tighter">${slice.currentVal.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className={`font-black text-sm min-w-[50px] text-right ${isHovered ? 'text-indigo-400' : themeClasses.textMain}`}>
                        {slice.percent.toFixed(1)}%
                      </span>
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: slice.color }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
            {slices.length === 0 && (
              <div className="h-full py-20 flex items-center justify-center">
                 <p className={`${themeClasses.textMuted} italic text-[10px] uppercase tracking-widest`}>Sin tenencias activas</p>
              </div>
            )}
          </div>
        </div>

        <div className={`p-4 ${theme === 'dark' ? 'bg-white/[0.02]' : 'bg-slate-50'} border-t ${theme === 'dark' ? 'border-[#20232a]' : 'border-slate-100'} text-center`}>
           <div className="flex items-center justify-center space-x-3 text-[9px] font-black text-slate-500 uppercase tracking-[0.2em]">
              <DollarSign size={12} className="text-indigo-500" />
              <span>Cálculo basado en el valor neto de mercado actual</span>
           </div>
        </div>
      </div>
    </div>
  );
};

export default HoldingsModal;
